import React from 'react';
import { Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Alert } from '@mui/material';
import { BENCH_TYPES, getBenchTypeLabel, SALARY_PERIODS, getSalaryPeriodLabel, SALARY_AMOUNT_TYPES, getSalaryAmountTypeLabel } from '../../../constants/benchTypes';
import RichTextEditor from '../RichTextEditor';
import EmployeeSearch from '../EmployeeSearch';
import { EmployeeSearchProvider } from '../EmployeeSearch/contexts/EmployeeSearchProvider';
import { EmployeeAvatar } from '../EmployeeAvatar';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';

/**
 * Reusable bench data form fields shared between EditBenchDataModal,
 * BenchDetailsStep (attach wizard), and the transfer wizard.
 *
 * Renders bench type, top talent switch, current manager (read-only),
 * bench manager search, comments, and salary fields (permission-gated).
 *
 * @param {Object} props
 * @param {string} props.benchType
 * @param {Function} props.setBenchType
 * @param {boolean} props.flagAsStar
 * @param {Function} props.setFlagAsStar
 * @param {string} props.comments
 * @param {Function} props.setComments
 * @param {Object|null} props.benchManager - Currently selected bench manager
 * @param {Function} props.setBenchManager
 * @param {Object|null} props.directManager - Employee's current org manager (read-only)
 * @param {string} props.salary
 * @param {Function} props.setSalary
 * @param {string} props.salaryCurrency
 * @param {Function} props.setSalaryCurrency
 * @param {string} props.salaryPeriod
 * @param {Function} props.setSalaryPeriod
 * @param {string} props.salaryAmountType
 * @param {Function} props.setSalaryAmountType
 * @param {boolean} [props.disabled=false] - Disable all controls (e.g. during submission)
 * @param {string} [props.instanceId='bench-data-fields'] - Unique ID for EmployeeSearchProvider
 */
const BenchDataFields = ({
  benchType,
  setBenchType,
  flagAsStar,
  setFlagAsStar,
  comments,
  setComments,
  benchManager,
  setBenchManager,
  directManager,
  salary,
  setSalary,
  salaryCurrency,
  setSalaryCurrency,
  salaryPeriod,
  setSalaryPeriod,
  salaryAmountType,
  setSalaryAmountType,
  disabled = false,
  instanceId = 'bench-data-fields',
  // ADR-117 §1 — true when a salary IS recorded but this environment cannot decrypt
  // it. Distinct from "no salary recorded": the field renders empty in both cases, so
  // without this the operator cannot tell them apart and may re-enter a value that was
  // never lost.
  salaryUnreadable = false,
  // Optional: a salary the caller can offer as a one-click fill (e.g. the pay rate a
  // source feed reported for this person). { amount, currency, period, amountType,
  // label } — `label` is the human text shown next to the action. Nothing is written
  // until the form is saved.
  salarySuggestion = null,
}) => {
  const ability = useAbility();
  const canReadSalary = ability.can(ACTIONS.READ, SUBJECTS.BENCH_SALARY);
  const canUpdateSalary = ability.can(ACTIONS.UPDATE, SUBJECTS.BENCH_SALARY);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <FormControl fullWidth size="small">
        <InputLabel shrink>Bench Type</InputLabel>
        <Select
          value={benchType}
          label="Bench Type"
          notched
          onChange={(e) => setBenchType(e.target.value)}
          disabled={disabled}
        >
          {Object.values(BENCH_TYPES).map((type) => (
            <MenuItem key={type} value={type}>
              {getBenchTypeLabel(type)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={flagAsStar}
            onChange={(e) => setFlagAsStar(e.target.checked)}
            disabled={disabled}
            color="warning"
          />
        }
        label="Top talent"
      />

      {directManager && (
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5, color: '#475569', fontWeight: 500 }}>
            Current Representative
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, backgroundColor: '#F8FAFC', borderRadius: 1, border: '1px solid #E2E8F0' }}>
            <EmployeeAvatar employee={directManager} size={28} />
            <Typography variant="body2" sx={{ color: '#334155' }}>
              {directManager.fullName}
            </Typography>
          </Box>
        </Box>
      )}

      <Box>
        <Typography variant="body2" sx={{ mb: 0.5, color: '#475569', fontWeight: 500 }}>
          Bench Representative <span style={{ color: '#94A3B8', fontWeight: 400 }}>(optional)</span>
        </Typography>
        <EmployeeSearchProvider instanceId={instanceId}>
          <EmployeeSearch
            value={benchManager}
            onChange={setBenchManager}
            label=""
            placeholder="Search bench representative..."
            hideLabel
            width={9999}
            showSelectedAvatar
            disabled={disabled}
            sx={{ width: '100% !important' }}
          />
        </EmployeeSearchProvider>
      </Box>

      <Box>
        <Typography variant="body2" sx={{ mb: 0.5, color: '#475569', fontWeight: 500 }}>
          Comments
        </Typography>
        <RichTextEditor
          variant="compact"
          value={comments}
          onChange={setComments}
          disabled={disabled}
          minHeight={120}
        />
      </Box>

      {canReadSalary && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ color: '#475569' }}>
              Salary Information
            </Typography>
            {salarySuggestion && canUpdateSalary && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: disabled ? 'default' : 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                onClick={() => {
                  if (disabled) {
                    return;
                  }
                  setSalary(salarySuggestion.amount ?? '');
                  setSalaryCurrency(salarySuggestion.currency || '');
                  setSalaryPeriod(salarySuggestion.period || '');
                  setSalaryAmountType(salarySuggestion.amountType || '');
                }}
              >
                {salarySuggestion.label || 'Use suggested salary'}
              </Typography>
            )}
          </Box>

          {salaryUnreadable && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              A salary is recorded for this individual but cannot be decrypted in this
              environment. It has not been lost. Leave the amount blank to keep it as it
              is — saving an empty amount will not erase it. Enter a value only if you
              intend to replace it.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Amount"
              type="number"
              value={salary}
              onChange={(e) => setSalary(e.target.value)}
              disabled={disabled || !canUpdateSalary}
              size="small"
              sx={{ flex: 2 }}
              placeholder={salaryUnreadable ? 'Recorded, but unreadable here' : 'e.g., 8000'}
            />
            <TextField
              label="Currency"
              value={salaryCurrency}
              onChange={(e) => setSalaryCurrency(e.target.value.toUpperCase().slice(0, 3))}
              disabled={disabled || !canUpdateSalary}
              size="small"
              sx={{ flex: 1, minWidth: 80 }}
              placeholder="EUR"
              inputProps={{ maxLength: 3 }}
            />
            <FormControl size="small" sx={{ flex: 1.5 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={salaryPeriod}
                label="Period"
                onChange={(e) => setSalaryPeriod(e.target.value)}
                disabled={disabled || !canUpdateSalary}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.values(SALARY_PERIODS).map((period) => (
                  <MenuItem key={period} value={period}>
                    {getSalaryPeriodLabel(period).replace('/', '')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1.5 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={salaryAmountType}
                label="Type"
                onChange={(e) => setSalaryAmountType(e.target.value)}
                disabled={disabled || !canUpdateSalary}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.values(SALARY_AMOUNT_TYPES).map((type) => (
                  <MenuItem key={type} value={type}>
                    {getSalaryAmountTypeLabel(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BenchDataFields;
